// Orders page with BiuroPaczka integration

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  Divider,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  List
} from '@mui/material';
import {
  Add as AddIcon,
  ShoppingCart as CartIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import biuropaczkaData from '@/data/biuropaczka_products.json';

// Mock order data
const generateMockOrders = () => {
  const statuses = ['delivered', 'shipped', 'processing', 'pending', 'cancelled'];
  const orders = [];
  
  for (let i = 1; i <= 15; i++) {
    const randomProducts = biuropaczkaData.products
      .sort(() => 0.5 - Math.random())
      .slice(0, Math.floor(Math.random() * 5) + 1);
    
    const items = randomProducts.map(product => ({
      id: product.id,
      name: product.name,
      price: Math.floor(Math.random() * 100) + 10, // Generate random price 10-110 zł
      quantity: Math.floor(Math.random() * 3 + 1),
      imageUrl: product.image_url // Fix: use image_url from JSON
    }));

    const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    orders.push({
      id: `BP${String(i).padStart(4, '0')}`,
      date: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000),
      status: statuses[Math.floor(Math.random() * statuses.length)],
      total,
      items,
      supplier: 'biuropaczka',
      trackingNumber: Math.random() > 0.5 ? `BP${Math.random().toString(36).substr(2, 9).toUpperCase()}` : null
    });
  }
  
  return orders.sort((a, b) => b.date.getTime() - a.date.getTime());
};

const mockOrders = generateMockOrders();

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function OrdersPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [quickOrderDialogOpen, setQuickOrderDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Quick order state
  const [quickOrderItems, setQuickOrderItems] = useState<any[]>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'shipped': return 'info';
      case 'processing': return 'warning';
      case 'pending': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'delivered': return 'Dostarczone';
      case 'shipped': return 'Wysłane';
      case 'processing': return 'W realizacji';
      case 'pending': return 'Oczekuje';
      case 'cancelled': return 'Anulowane';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckIcon />;
      case 'shipped': return <ShippingIcon />;
      case 'processing': return <RefreshIcon />;
      case 'pending': return <PendingIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <PendingIcon />;
    }
  };

  // Filter orders
  const filteredOrders = useMemo(() => {
    return mockOrders.filter(order => {
      const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.items.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = !statusFilter || order.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, statusFilter]);

  // Stats
  const orderStats = useMemo(() => {
    const total = mockOrders.length;
    const pending = mockOrders.filter(o => o.status === 'pending').length;
    const processing = mockOrders.filter(o => o.status === 'processing').length;
    const totalValue = mockOrders.reduce((sum, order) => sum + order.total, 0);
    
    return { total, pending, processing, totalValue };
  }, []);

  const handleQuickOrder = () => {
    // Mock quick order functionality
    alert('Funkcja szybkiego zamówienia zostanie zintegrowna z API BiuroPaczka');
    setQuickOrderDialogOpen(false);
    setQuickOrderItems([]);
  };

  const addQuickOrderItem = (product: any) => {
    setQuickOrderItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { 
        ...product, 
        quantity: 1,
        imageUrl: product.image_url // Fix imageUrl mapping
      }];
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Zamówienia
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => setQuickOrderDialogOpen(true)}
          >
            Szybkie zamówienie
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Historia zamówień i zarządzanie zakupami BiuroPaczka
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #339af0 0%, #228be6 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CartIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {orderStats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Łącznie zamówień
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ffd43b 0%, #fab005 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PendingIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {orderStats.pending}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Oczekujące
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <RefreshIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {orderStats.processing}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                W realizacji
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <DownloadIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {orderStats.totalValue.toLocaleString()} zł
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Wartość zamówień
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* BiuroPaczka Integration Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Integracja BiuroPaczka:</strong> Wszystkie zamówienia są automatycznie 
          synchronizowane z BiuroPaczka.pl. Możesz śledzić status i zarządzać dostawami bezpośrednio z tej strony.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="orders tabs">
          <Tab label="Historia zamówień" />
          <Tab label="Szybkie zamówienie" />
          <Tab label="Śledzenie dostaw" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Orders Table */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Szukaj zamówień..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    <MenuItem value="pending">Oczekujące</MenuItem>
                    <MenuItem value="processing">W realizacji</MenuItem>
                    <MenuItem value="shipped">Wysłane</MenuItem>
                    <MenuItem value="delivered">Dostarczone</MenuItem>
                    <MenuItem value="cancelled">Anulowane</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nr zamówienia</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Produkty</TableCell>
                  <TableCell align="right">Wartość</TableCell>
                  <TableCell align="center">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{order.id}</Typography>
                      {order.trackingNumber && (
                        <Typography variant="caption" color="text.secondary">
                          Tracking: {order.trackingNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {order.date.toLocaleDateString('pl-PL')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={getStatusLabel(order.status)}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {order.items.length} produkt{order.items.length !== 1 ? 'ów' : ''}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                        {order.total.toLocaleString()} zł
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small">
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Quick Order */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Najczęściej zamawiane produkty
              </Typography>
              <Grid container spacing={2}>
                {biuropaczkaData.products.slice(0, 6).map((product, index) => {
                  const productPrice = 15 + (index * 12); // Fixed price for each product
                  return (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Card sx={{ cursor: 'pointer' }} onClick={() => addQuickOrderItem({ ...product, price: productPrice })}>
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                backgroundImage: `url(${product.image_url})`,
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            />
                            <Box flex={1}>
                              <Typography variant="subtitle2">
                                {product.name.substring(0, 40)}...
                              </Typography>
                              <Typography variant="body2" color="primary">
                                {productPrice} zł
                              </Typography>
                            </Box>
                            <IconButton size="small" color="primary">
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Koszyk
                  </Typography>
                  {quickOrderItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Koszyk jest pusty
                    </Typography>
                  ) : (
                    <>
                      <List>
                        {quickOrderItems.map((item) => (
                          <ListItem key={item.id} dense>
                            <ListItemAvatar>
                              <Avatar 
                                sx={{ width: 32, height: 32 }}
                                src={item.imageUrl}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              primary={item.name.substring(0, 30)}
                              secondary={`${item.quantity}x ${item.price} zł`}
                            />
                          </ListItem>
                        ))}
                      </List>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography fontWeight="medium">Łącznie:</Typography>
                        <Typography fontWeight="medium">
                          {quickOrderItems.reduce((sum, item) => sum + ((item.price || 0) * item.quantity), 0).toLocaleString()} zł
                        </Typography>
                      </Box>
                      <Button 
                        variant="contained" 
                        fullWidth 
                        startIcon={<CartIcon />}
                        onClick={handleQuickOrder}
                      >
                        Złóż zamówienie
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Tracking */}
          <Typography variant="h6" gutterBottom>
            Śledzenie dostaw
          </Typography>
          <Grid container spacing={2}>
            {mockOrders
              .filter(order => order.status === 'shipped' && order.trackingNumber)
              .slice(0, 5)
              .map((order) => (
                <Grid item xs={12} md={6} key={order.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justify-content="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {order.id}
                        </Typography>
                        <Chip 
                          icon={<ShippingIcon />}
                          label="Wysłane" 
                          color="info" 
                          size="small" 
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Numer przesyłki: {order.trackingNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Przewidywana dostawa: {new Date(order.date.getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('pl-PL')}
                      </Typography>
                      <Button variant="outlined" size="small" fullWidth>
                        Śledź przesyłkę
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </TabPanel>
      </Card>

      {/* Order Details Modal */}
      <Dialog 
        open={!!selectedOrder} 
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Szczegóły zamówienia {selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Data zamówienia:</Typography>
                  <Typography>{selectedOrder.date.toLocaleDateString('pl-PL')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    icon={getStatusIcon(selectedOrder.status)}
                    label={getStatusLabel(selectedOrder.status)}
                    color={getStatusColor(selectedOrder.status) as any}
                    size="small"
                  />
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom>Produkty:</Typography>
              <List>
                {selectedOrder.items.map((item: any) => (
                  <ListItem key={item.id}>
                    <ListItemAvatar>
                      <Avatar src={item.imageUrl} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={item.name}
                      secondary={`Ilość: ${item.quantity} | Cena: ${item.price ? item.price.toLocaleString() : '0'} zł`}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Łączna wartość:</Typography>
                <Typography variant="h6" color="primary">
                  {selectedOrder.total.toLocaleString()} zł
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)}>Zamknij</Button>
          <Button variant="contained">Pobierz fakturę</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}